import { useEffect, useRef, useState } from 'react';
import loadLeaflet from './loadLeaflet';

const ISFAHAN = { lat: 32.6546, lng: 51.6680 };

// Click-to-place (and drag-to-adjust) marker on an OpenStreetMap tile
// layer. `lat`/`lng` are controlled from outside (e.g. manually-typed
// coordinate inputs) - external changes reposition the marker/view, but
// only when they didn't originate from this component's own click/drag
// (movingRef guards against fighting the user mid-drag).
export default function LeafletMapPicker({ lat, lng, onChange, height = 320 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const movingRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !containerRef.current || mapRef.current) return;
        const hasInitial = lat && lng && !isNaN(Number(lat)) && !isNaN(Number(lng));
        const start = hasInitial ? { lat: Number(lat), lng: Number(lng) } : ISFAHAN;
        const map = L.map(containerRef.current).setView([start.lat, start.lng], hasInitial ? 15 : 11);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
          maxZoom: 19,
        }).addTo(map);

        const placeMarker = (latlng, fromUser) => {
          if (markerRef.current) map.removeLayer(markerRef.current);
          markerRef.current = L.marker(latlng, { draggable: true }).addTo(map);
          markerRef.current.on('dragend', () => {
            const p = markerRef.current.getLatLng();
            movingRef.current = true;
            onChange?.(p.lat, p.lng);
          });
          if (fromUser) { movingRef.current = true; onChange?.(latlng.lat, latlng.lng); }
        };

        if (hasInitial) placeMarker(start, false);
        map.on('click', (e) => placeMarker(e.latlng, true));

        mapRef.current = map;
        setReady(true);
        // See LeafletRouteMap.jsx - Leaflet measures container size once at
        // init, which can be 0x0 if this mounted inside a grid/flex column
        // whose width wasn't settled yet, leaving the map blank forever.
        requestAnimationFrame(() => mapRef.current?.invalidateSize());
        setTimeout(() => mapRef.current?.invalidateSize(), 200);
      })
      .catch(() => setFailed(true));

    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reposition when lat/lng change from outside (e.g. the user typed
  // coordinates directly) - skip the update that this component itself
  // just triggered via click/drag, or the marker would visibly jitter.
  useEffect(() => {
    if (movingRef.current) { movingRef.current = false; return; }
    const map = mapRef.current;
    const L = window.L;
    if (!map || !L || !lat || !lng || isNaN(Number(lat)) || isNaN(Number(lng))) return;
    const latlng = { lat: Number(lat), lng: Number(lng) };
    if (markerRef.current) map.removeLayer(markerRef.current);
    markerRef.current = L.marker(latlng, { draggable: true }).addTo(map);
    markerRef.current.on('dragend', () => {
      const p = markerRef.current.getLatLng();
      movingRef.current = true;
      onChange?.(p.lat, p.lng);
    });
    map.setView([latlng.lat, latlng.lng], map.getZoom());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  return (
    <div>
      <div ref={containerRef} style={{ width: '100%', height, borderRadius: 10, overflow: 'hidden', border: '1.5px solid #e2e8f0', background: '#f8faff' }} />
      {failed && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>خطا در بارگذاری نقشه - اتصال اینترنت را بررسی کنید</div>}
      {!ready && !failed && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>در حال بارگذاری نقشه...</div>}
      {ready && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>روی نقشه کلیک کنید تا موقعیت انتخاب شود (یا نشانگر را جابجا کنید)</div>}
    </div>
  );
}
